import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectOAuthCredentials } from "@carto/react-redux";

/**
 * @param {*} opts - configuration options
 * @param {any} opts.initialData - data to return before fetching is done
 * @param {function} opts.callback - function to call for data fetching
 * @param {boolean} use404 - flag that indicates if we should redirect the app to /404 if the returned data is falsy
 * @param {boolean} enabled - flag that indicates if the effect is executed
 * @param {string} errorMsg - message to use for `console.error` in the `catch` block of the fetching process
 *
 * example usage:
 * ```
  const { data: details } = useDataFetch({
    initialData: { id },
    use404: true,
    errorMsg: '[PoiDetail.js] Error fetching poi detail'
    callback: useCallback((opts) => getFeatureDetails({ id }, opts), [id]),
  })
 * ```
 */
export function useDataFetch({
  initialData,
  use404 = false,
  errorMsg = "[useDataFetch] Error in data fetch",
  enabled = true,
  callback = () => {},
}) {
  const navigate = useNavigate();
  //const credentials = useSelector(selectOAuthCredentials);
  const credentials = useSelector((state) => state.carto.credentials2);
  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // abort flow taken from here: https://github.com/CartoDB/carto-react-lib/blob/master/src/widgets/FormulaWidget.js#L29
    const abortController = new AbortController();

    async function dataFetch() {
      setError(null);
      setLoading(true);

      try {
        const data = await callback({ abortController, credentials });
        if (use404 && !data) {
          navigate("/404", { replace: true });
          return;
        }

        setData(data);
        setLoading(false);
      } catch (err) {
        if (err.name !== "AbortError") {
          if (use404 && err.status === 404) {
            navigate("/404", { replace: true });
          }
          // eslint-disable-next-line
          console.error(errorMsg, err);
          setError(err);
          setLoading(false);
        }
      }
    }

    if (enabled) {
      dataFetch();
    }

    return function cleanup() {
      abortController.abort();
    };
  }, [credentials, enabled, callback, use404, errorMsg, navigate]);

  return { data, loading, error };
}
